import { useState, useEffect, useMemo } from 'react';
import { Modal } from '@heroui/react';
import { Search, Users, FolderKanban, FileText, MonitorPlay, Settings, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useQuery } from '@tanstack/react-query';

interface CmdUser {
  id: string
  full_name?: string | null
  email?: string | null
}

interface CmdProject {
  id: string
  name?: string | null
}

export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: users = [] } = useQuery<CmdUser[]>({
    queryKey: ['cmdUsers'],
    queryFn: async () => {
      const res = await apiClient.get('/users');
      const list = res.data.data || [];
      return Array.isArray(list) ? list : [];
    },
    enabled: isOpen,
  });

  const { data: projects = [] } = useQuery<CmdProject[]>({
    queryKey: ['cmdProjects'],
    queryFn: async () => {
      const res = await apiClient.get('/projects');
      const list = res.data || [];
      return Array.isArray(list) ? list : [];
    },
    enabled: isOpen,
  });

  const quickLinks = [
    { label: 'Overview Dashboard', path: '/', icon: <Activity size={16} /> },
    { label: 'Live View', path: '/live', icon: <MonitorPlay size={16} /> },
    { label: 'Reports', path: '/reports', icon: <FileText size={16} /> },
    { label: 'Settings', path: '/settings', icon: <Settings size={16} /> },
  ];

  const filteredUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    const q = search.toLowerCase();
    return list.filter((u) =>
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    ).slice(0, 3);
  }, [users, search]);

  const filteredProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const q = search.toLowerCase();
    return list.filter((p) =>
      p.name?.toLowerCase().includes(q)
    ).slice(0, 3);
  }, [projects, search]);

  const handleSelect = (path: string) => {
    navigate(path);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <Modal>
      <Modal.Backdrop isOpen={isOpen} onOpenChange={setIsOpen} className="bg-black dark:bg-white/60 backdrop-blur-sm">
        <Modal.Container placement="top" className="pt-[15vh]">
          <Modal.Dialog className="w-full max-w-2xl bg-surface rounded-2xl shadow-2xl overflow-hidden border border-border mx-4">
            <div className="border-b border-border p-4 flex items-center gap-3">
              <Search className="text-muted" size={20} />
              <input 
                autoFocus
                placeholder="Search employees, projects, or type a command..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-lg text-foreground outline-none placeholder-muted"
              />
              <kbd className="hidden sm:inline-block bg-surface-secondary text-muted px-2 py-1 rounded text-xs border border-border-secondary">ESC</kbd>
            </div>
            <div className="p-0 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="p-2">
                {search === '' && (
                  <div className="mb-4">
                    <p className="px-4 py-2 text-xs font-semibold text-muted uppercase tracking-wider">Quick Links</p>
                    {quickLinks.map((link) => (
                      <button 
                        key={link.path}
                        onClick={() => handleSelect(link.path)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-surface-secondary transition-colors text-left"
                      >
                        <div className="text-muted">{link.icon}</div>
                        <span className="text-foreground font-medium">{link.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {(search !== '' || filteredUsers.length > 0) && (
                  <div className="mb-4">
                    <p className="px-4 py-2 text-xs font-semibold text-muted uppercase tracking-wider">Employees</p>
                    {filteredUsers.length === 0 && search !== '' && (
                      <p className="px-4 py-2 text-sm text-muted">No employees found.</p>
                    )}
                    {filteredUsers.map((user) => (
                      <button 
                        key={user.id}
                        onClick={() => handleSelect(`/employees/${user.id}`)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-surface-secondary transition-colors text-left"
                      >
                        <Users size={16} className="text-muted" />
                        <div>
                          <p className="font-medium text-foreground">{user.full_name}</p>
                          <p className="text-xs text-muted">{user.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {(search !== '' || filteredProjects.length > 0) && (
                  <div className="mb-4">
                    <p className="px-4 py-2 text-xs font-semibold text-muted uppercase tracking-wider">Projects</p>
                    {filteredProjects.length === 0 && search !== '' && (
                      <p className="px-4 py-2 text-sm text-muted">No projects found.</p>
                    )}
                    {filteredProjects.map((project) => (
                      <button 
                        key={project.id}
                        onClick={() => handleSelect(`/projects/${project.id}`)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-surface-secondary transition-colors text-left"
                      >
                        <FolderKanban size={16} className="text-muted" />
                        <span className="font-medium text-foreground">{project.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
